/**
 * spaceBossDrawBuilders.ts
 * Boss-specific draw-rect builders for the World 2 Void Mothership —
 * hull, turret bolts, homing missiles, and the shield-ring nodes.
 *
 * Extracted from `spaceDrawBuilders.ts` so that module stays under the
 * AGENTS.md 300-line cap. `spaceDrawBuilders.ts` re-exports every symbol
 * from here so existing consumers keep a single import surface
 * (`./spaceDrawBuilders`) without churn — new code may import directly.
 *
 * No game-state mutation — every builder is read-only over `spaceState`.
 *
 * @module spaceBossDrawBuilders
 */

import { spaceState } from '../engine/spaceState';
import {
  SPACE_ENEMY_BOSS_COLOR,
  SPACE_BOSS_PROJECTILE_COLOR,
  SPACE_BOSS_MISSILE_COLOR,
} from '../../constants/rendering';
import {
  VOID_MOTHERSHIP_SHIELD_NODE_COUNT,
  VOID_MOTHERSHIP_SHIELD_NODE_SIZE,
  VOID_MOTHERSHIP_SHIELD_RADIUS_X,
  VOID_MOTHERSHIP_SHIELD_RADIUS_Y,
} from '../../constants/bossConstants';
import type { SpaceRect } from './spaceRenderTypes';

/**
 * Hull fill applied during the brief hit-flash window after an
 * EXPOSED-window projectile lands. Bright cyan, high contrast against
 * the default `SPACE_ENEMY_BOSS_COLOR` so a single-frame flash is
 * still legible at 60 fps. Local to this file — the only consumer is
 * `buildBossRect`.
 */
const SPACE_BOSS_HIT_FLASH_COLOR = '#fdfdff';

/**
 * Build the camera-relative draw rectangle for the World 2 boss, or
 * `null` when no boss is live.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildBossRect(cameraX: number): SpaceRect | null {
  const boss = spaceState.boss;
  if (boss === null || boss.dead) return null;
  return {
    x: boss.x - cameraX,
    y: boss.y,
    width: boss.width,
    height: boss.height,
    // DECISION: Flash overrides the default hull fill while
    // `hitFlashTimer > 0`. Renderer-only read of the entity field —
    // matches AGENTS.md "renderers never mutate game state".
    color:
      boss.hitFlashTimer > 0
        ? SPACE_BOSS_HIT_FLASH_COLOR
        : SPACE_ENEMY_BOSS_COLOR,
    key: 'boss',
  };
}

/**
 * Build camera-relative rectangles for every live boss turret projectile.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildBossProjectileRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  for (const p of spaceState.bossProjectiles) {
    if (p.dead) continue;
    out.push({
      x: p.x - cameraX,
      y: p.y,
      width: p.width,
      height: p.height,
      color: SPACE_BOSS_PROJECTILE_COLOR,
      // DECISION: Prefix differs from ship projectiles' `p` so debug
      // logs / React keys never collide across the two streams.
      key: `bp${p.spawnId}`,
    });
  }
  return out;
}

/**
 * Build camera-relative rectangles for every live boss homing missile.
 * Sibling to `buildBossProjectileRects` — same shape, different source
 * array and palette. Greybox rect only; trail / flame VFX deferred.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildBossMissileRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  for (const m of spaceState.bossMissiles) {
    if (m.dead) continue;
    out.push({
      x: m.x - cameraX,
      y: m.y,
      width: m.width,
      height: m.height,
      color: SPACE_BOSS_MISSILE_COLOR,
      // DECISION: `bm` prefix keeps missile keys distinct from the `bp`
      // turret-bolt stream and the `p` ship-bolt stream.
      key: `bm${m.spawnId}`,
    });
  }
  return out;
}

/**
 * Pale-cyan fill for the boss shield-ring nodes. Lives next to its sole
 * consumer rather than `rendering.ts` because the rest of the renderer
 * doesn't need it — promote to the shared palette the moment a second
 * caller appears.
 */
const SPACE_BOSS_SHIELD_NODE_COLOR = '#9be8ff';

/**
 * Build camera-relative rectangles for the boss shield-ring nodes.
 *
 * Returns the empty array unless the boss is alive, in HOLD, and
 * currently SHIELDED — i.e. exactly when the shield is gameplay-active
 * and {@link VoidMothership.takeDamage} no-ops. The renderer then sees a
 * visible ring of orbiting nodes that matches the damage gate one-to-one.
 *
 * Each node sits at `(cx + radiusX·cos(angle + i·2π/N),
 *                      cy + radiusY·sin(angle + i·2π/N))` minus half
 * `..._NODE_SIZE` so the rect is centred on the orbit. The renderer
 * draws plain axis-aligned squares — the rotation is visible as the
 * orbiting positions, not via per-rect rotation.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildBossShieldRects(cameraX: number): SpaceRect[] {
  const boss = spaceState.boss;
  if (boss === null || boss.dead) return [];
  if (boss.state !== 'HOLD' || !boss.isShielded) return [];
  const cx = boss.x + boss.width / 2;
  const cy = boss.y + boss.height / 2;
  const half = VOID_MOTHERSHIP_SHIELD_NODE_SIZE / 2;
  const out: SpaceRect[] = [];
  for (let i = 0; i < VOID_MOTHERSHIP_SHIELD_NODE_COUNT; i++) {
    const a =
      boss.shieldAngle +
      (i * Math.PI * 2) / VOID_MOTHERSHIP_SHIELD_NODE_COUNT;
    out.push({
      x: cx - cameraX + Math.cos(a) * VOID_MOTHERSHIP_SHIELD_RADIUS_X - half,
      y: cy + Math.sin(a) * VOID_MOTHERSHIP_SHIELD_RADIUS_Y - half,
      width: VOID_MOTHERSHIP_SHIELD_NODE_SIZE,
      height: VOID_MOTHERSHIP_SHIELD_NODE_SIZE,
      color: SPACE_BOSS_SHIELD_NODE_COLOR,
      // DECISION: Same shared sprite for every node — orbit position
      // is the visual variation, not per-node art. Renderer falls
      // back to the colour rect when the sprite hasn't decoded.
      spriteId: 'shield-node',
      // DECISION: `bs` prefix keeps shield-node keys distinct from the
      // bolt (`bp`), missile (`bm`), and mine (`m`) streams.
      key: `bs${i}`,
    });
  }
  return out;
}
